import { DragDropContext } from "@hello-pangea/dnd";
import { Button, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Chip, Divider, Box, IconButton } from "@mui/material";
import React, { useState } from "react";
import styled from "styled-components";
import AppBarComponent from "../components/AppBarComponent";
import CreateTaskModal from "../components/tasks/CreateTaskModal";
import EditTaskModal from "../components/tasks/EditTaskModal";
import TaskList from "../components/tasks/TaskList";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import CategoryIcon from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 24px;
  background: ${({ theme }) => theme.palette.background.default};
  min-height: calc(100vh - 64px);
  gap: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 16px 24px;
  background: ${({ theme }) => theme.palette.background.paper};
  box-shadow: ${({ theme }) => theme.shadows[1]};
`;

const TaskDetailModal = ({ open, handleClose, task }) => {
  const theme = useTheme();

  if (!task) return null;

  const getPriorityColor = () => {
    switch(task.priority) {
      case 'Alta': return theme.palette.error.main;
      case 'Média': return theme.palette.warning.main;
      case 'Baixa': return theme.palette.success.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getStatusColor = () => {
    switch(task.status) {
      case 'Aguardando': return theme.palette.warning.main;
      case 'Em progresso': return theme.palette.info.main;
      case 'Concluída': return theme.palette.success.main;
      default: return theme.palette.text.secondary;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[24],
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
        padding: "20px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <Typography variant="h6" sx={{ fontSize: "1.25rem", letterSpacing: "0.5px" }}>
          Detalhes da Tarefa
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: "inherit" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Header Info */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{task.title}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={task.priority} 
                size="small"
                sx={{ 
                  backgroundColor: getPriorityColor(),
                  color: theme.palette.getContrastText(getPriorityColor()),
                  fontWeight: 600
                }}
              />
              <Chip 
                label={task.status} 
                size="small"
                sx={{ 
                  backgroundColor: getStatusColor(),
                  color: theme.palette.getContrastText(getStatusColor()),
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />

          {/* Description */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <DescriptionIcon fontSize="small" />
              Descrição
            </Typography>
            <Typography variant="body1" sx={{ 
              backgroundColor: theme.palette.action.hover,
              padding: 2,
              borderRadius: '8px'
            }}>
              {task.description}
            </Typography>
          </Box>

          {/* Client */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <PersonIcon fontSize="small" />
              Cliente
            </Typography>
            <Typography variant="body1">{task.client}</Typography>
          </Box>

          {/* Date */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <EventIcon fontSize="small" />
              Data de Vencimento
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
              <Typography variant="body1">
                {task.dueDate ? format(new Date(task.dueDate), 'PPP', { locale: ptBR }) : 'Não definida'}
              </Typography>
            </LocalizationProvider>
          </Box>

          {/* Category */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <CategoryIcon fontSize="small" />
              Categoria
            </Typography>
            <Chip 
              label={task.category} 
              sx={{ 
                backgroundColor: theme.palette.action.selected,
                color: theme.palette.text.primary
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        padding: "16px 24px",
        borderTop: `1px solid ${theme.palette.divider}`
      }}>
        <Button 
          onClick={handleClose} 
          variant="contained"
          sx={{
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            "&:hover": {
              backgroundColor: theme.palette.action.hover
            },
            fontWeight: 600,
            borderRadius: "10px",
            padding: "8px 16px",
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TaskBoard = () => {
  const theme = useTheme();
  const [tasks, setTasks] = useState({
    backlog: [
      {
        id: "task-1",
        title: "Realocação de João",
        description: "Documentação para realocação.",
        status: "Aguardando",
        client: "João Silva",
        priority: "Alta",
        dueDate: "2023-12-15",
        category: "Documentação"
      },
      {
        id: "task-2",
        title: "Realocação de Maria",
        description: "Organizar o visto de trabalho.",
        status: "Aguardando",
        client: "Maria Oliveira",
        priority: "Média",
        dueDate: "2023-12-20",
        category: "Visto"
      },
    ],
    inProgress: [
      {
        id: "task-3",
        title: "Realocação de José",
        description: "Transferência bancária.",
        status: "Em progresso",
        client: "José Santos",
        priority: "Alta",
        dueDate: "2023-12-10",
        category: "Financeiro"
      },
    ],
    done: [
      {
        id: "task-4",
        title: "Realocação de Pedro",
        description: "Confirmar instalação em Lisboa.",
        status: "Concluída",
        client: "Pedro Almeida",
        priority: "Baixa",
        dueDate: "2023-11-30",
        category: "Logística"
      },
    ],
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskToEdit, setTaskToEdit] = useState(null);

  const openCreateModal = () => setIsCreateModalOpen(true);
  const closeCreateModal = () => setIsCreateModalOpen(false);

  const openEditModal = (task) => {
    setTaskToEdit(task);
    setIsEditModalOpen(true);
  };
  
  const closeEditModal = () => {
    setTaskToEdit(null);
    setIsEditModalOpen(false);
  };

  const openDetailModal = (task) => {
    setSelectedTask(task);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setSelectedTask(null);
    setIsDetailModalOpen(false);
  };

  const saveTask = (task) => {
    setTasks((prevTasks) => ({
      ...prevTasks,
      backlog: [...prevTasks.backlog, { 
        ...task, 
        id: `task-${Date.now()}`,
        status: "Aguardando"
      }],
    }));
    closeCreateModal();
  };

  const updateTask = (updatedTask) => {
    setTasks((prevTasks) => {
      const updatedColumns = Object.keys(prevTasks).reduce((result, column) => {
        result[column] = prevTasks[column].map((task) =>
          task.id === updatedTask.id ? { ...task, ...updatedTask } : task
        );
        return result;
      }, {});
      return updatedColumns;
    });
    closeEditModal();
  };

  const removeTask = (taskId) => {
    setTasks((prevTasks) => {
      const updatedColumns = Object.keys(prevTasks).reduce((result, column) => {
        result[column] = prevTasks[column].filter((task) => task.id !== taskId);
        return result;
      }, {});
      return updatedColumns;
    });
  };

  const onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;

    const sourceColumn = source.droppableId;
    const destinationColumn = destination.droppableId;

    const startTasks = Array.from(tasks[sourceColumn]);
    const finishTasks = Array.from(tasks[destinationColumn]);

    const [movedTask] = startTasks.splice(source.index, 1);

    // Atualiza o status baseado na coluna de destino
    let updatedStatus = movedTask.status;
    if (destinationColumn === 'backlog') updatedStatus = 'Aguardando';
    else if (destinationColumn === 'inProgress') updatedStatus = 'Em progresso';
    else if (destinationColumn === 'done') updatedStatus = 'Concluída';

    const updatedTask = { ...movedTask, status: updatedStatus };

    if (sourceColumn === destinationColumn) {
      startTasks.splice(destination.index, 0, updatedTask);
      setTasks((prevTasks) => ({
        ...prevTasks,
        [sourceColumn]: startTasks,
      }));
    } else {
      finishTasks.splice(destination.index, 0, updatedTask);
      setTasks((prevTasks) => ({
        ...prevTasks,
        [sourceColumn]: startTasks,
        [destinationColumn]: finishTasks,
      }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
      <DragDropContext onDragEnd={onDragEnd}>
        <AppBarComponent 
          title="Gestão de Tarefas Premium" 
          sx={{ 
            background: theme.palette.primary.main,
            boxShadow: theme.shadows[4]
          }} 
        />
        <ButtonContainer theme={theme}>
          <Button
            variant="contained"
            onClick={openCreateModal}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              "&:hover": { 
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.darker} 100%)`,
                transform: "translateY(-1px)",
                boxShadow: theme.shadows[6]
              },
              transition: "all 0.2s ease-in-out",
              fontWeight: 700,
              fontSize: "0.875rem",
              padding: "10px 24px",
              borderRadius: "10px",
              letterSpacing: "0.5px",
              textTransform: "uppercase"
            }}
          >
            + Criar Nova Tarefa
          </Button>
        </ButtonContainer>
        <Container theme={theme}>
          {Object.entries({
            backlog: "Aguardando",
            inProgress: "Em Progresso",
            done: "Concluído"
          }).map(([columnId, title]) => (
            <TaskList
              key={columnId}
              tasks={tasks[columnId]}
              columnId={columnId}
              title={title}
              onEdit={openEditModal}
              onRemove={removeTask}
              onView={openDetailModal}
            />
          ))}
        </Container>
        <CreateTaskModal
          open={isCreateModalOpen}
          handleClose={closeCreateModal}
          handleSave={saveTask}
        />
        {taskToEdit && (
          <EditTaskModal
            open={isEditModalOpen}
            handleClose={closeEditModal}
            task={taskToEdit}
            handleSave={updateTask}
          />
        )}
        {selectedTask && (
          <TaskDetailModal
            open={isDetailModalOpen}
            handleClose={closeDetailModal}
            task={selectedTask}
          />
        )}
      </DragDropContext>
    </LocalizationProvider>
  );
};

export default TaskBoard;